(() => {
  const minScale = 1;
  const maxScale = 4;
  const step = 0.25;

  const clampScale = (scale) => Math.min(maxScale, Math.max(minScale, scale));

  const getActiveImage = (modal) => modal.querySelector('.product-media-modal__content img.active');

  const applyTransform = (image) => {
    if (!image) return;

    const scale = Number(image.dataset.scale || 1);
    const panX = Number(image.dataset.panX || 0);
    const panY = Number(image.dataset.panY || 0);
    image.style.transform = `translate3d(${panX}px, ${panY}px, 0) scale(${scale})`;
  };

  const setImageState = (image, scale, panX, panY) => {
    if (!image) return;

    image.dataset.scale = String(clampScale(scale));
    image.dataset.panX = String(panX);
    image.dataset.panY = String(panY);

    if (Number(image.dataset.scale) === 1) {
      image.dataset.panX = '0';
      image.dataset.panY = '0';
    }

    applyTransform(image);
  };

  const resetImage = (image) => {
    setImageState(image, 1, 0, 0);
  };

  const zoomImage = (image, delta) => {
    if (!image) return;

    const currentScale = Number(image.dataset.scale || 1);
    setImageState(
      image,
      currentScale + delta,
      Number(image.dataset.panX || 0),
      Number(image.dataset.panY || 0)
    );
  };

  const setDownload = (modal) => {
    const activeImage = getActiveImage(modal);
    const download = modal.querySelector('[data-product-media-download]');
    if (!download) return;
    if (!activeImage) {
      download.hidden = true;
      download.removeAttribute('href');
      return;
    }

    const url = activeImage.currentSrc || activeImage.src;
    download.hidden = false;
    download.href = url;
    download.setAttribute('download', activeImage.alt ? `${activeImage.alt}.jpg` : 'product-image.jpg');
  };

  const setupModal = (modal) => {
    const content = modal.querySelector('.product-media-modal__content');
    const toolbar = modal.querySelector('.product-media-modal__toolbar');
    if (!content) return;

    const observer = new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => mutation.target !== content)) return;

      const activeImage = getActiveImage(modal);
      resetImage(activeImage);
      setDownload(modal);
    });

    observer.observe(content, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    modal.querySelectorAll('[data-product-media-zoom]').forEach((button) => {
      button.addEventListener('click', () => {
        const image = getActiveImage(modal);
        if (!image) return;

        const action = button.dataset.productMediaZoom;
        if (action === 'in') zoomImage(image, step);
        if (action === 'out') zoomImage(image, -step);
        if (action === 'reset') resetImage(image);
      });
    });

    toolbar?.addEventListener('pointerup', (event) => {
      if (!event.target.closest('[id^="ModalClose-"]')) event.stopPropagation();
    });

    content.addEventListener(
      'wheel',
      (event) => {
        const image = getActiveImage(modal);
        if (!image || event.target !== image) return;

        event.preventDefault();
        zoomImage(image, event.deltaY < 0 ? step : -step);
      },
      { passive: false }
    );

    content.addEventListener('dblclick', (event) => {
      const image = getActiveImage(modal);
      if (!image || event.target !== image) return;

      const currentScale = Number(image.dataset.scale || 1);
      setImageState(image, currentScale > 1 ? 1 : 2, 0, 0);
    });

    const pointers = new Map();
    let dragState = null;
    let pinchState = null;

    const getPinchDistance = () => {
      const points = Array.from(pointers.values());
      if (points.length < 2) return 0;

      return Math.hypot(points[0].clientX - points[1].clientX, points[0].clientY - points[1].clientY);
    };

    content.addEventListener('pointerdown', (event) => {
      const image = getActiveImage(modal);
      if (!image || event.target !== image) return;

      pointers.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY });
      image.setPointerCapture(event.pointerId);
      content.classList.add('is-dragging');

      if (pointers.size === 2) {
        pinchState = {
          image,
          startDistance: getPinchDistance(),
          startScale: Number(image.dataset.scale || 1)
        };
        dragState = null;
        return;
      }

      dragState = {
        image,
        startX: event.clientX,
        startY: event.clientY,
        panX: Number(image.dataset.panX || 0),
        panY: Number(image.dataset.panY || 0)
      };
    });

    content.addEventListener('pointermove', (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY });

      if (pinchState && pointers.size >= 2) {
        const nextScale = pinchState.startScale * (getPinchDistance() / pinchState.startDistance);
        setImageState(
          pinchState.image,
          nextScale,
          Number(pinchState.image.dataset.panX || 0),
          Number(pinchState.image.dataset.panY || 0)
        );
        return;
      }

      if (!dragState) return;

      const nextX = dragState.panX + event.clientX - dragState.startX;
      const nextY = dragState.panY + event.clientY - dragState.startY;
      setImageState(dragState.image, Number(dragState.image.dataset.scale || 1), nextX, nextY);
    });

    const finishPointer = (event) => {
      event.stopPropagation();
      pointers.delete(event.pointerId);

      if (pointers.size === 0) {
        dragState = null;
        pinchState = null;
        content.classList.remove('is-dragging');
        return;
      }

      if (pointers.size === 1) {
        const image = getActiveImage(modal);
        const remainingPoint = Array.from(pointers.values())[0];
        pinchState = null;
        dragState = image
          ? {
              image,
              startX: remainingPoint.clientX,
              startY: remainingPoint.clientY,
              panX: Number(image.dataset.panX || 0),
              panY: Number(image.dataset.panY || 0)
            }
          : null;
      }
    };

    content.addEventListener('pointerup', finishPointer);
    content.addEventListener('pointercancel', finishPointer);
  };

  document.querySelectorAll('.b2b-product-viewer').forEach(setupModal);
})();
